import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Truck, Plus, Edit, Ban, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SearchBar from '@/components/shared/SearchBar';
import { API, toast } from '@/App';
import { logClientError } from '@/utils/clientError';

const ManageLivreurs = () => {
  const [livreurs, setLivreurs] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingLivreur, setEditingLivreur] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    city: ''
  });

  // Filtered livreurs based on search
  const filteredLivreurs = useMemo(() => {
    if (!searchQuery) return livreurs;
    
    const query = searchQuery.toLowerCase();
    return livreurs.filter(livreur => 
      livreur.full_name.toLowerCase().includes(query) ||
      livreur.email.toLowerCase().includes(query) ||
      livreur.phone?.toLowerCase().includes(query) ||
      livreur.city?.toLowerCase().includes(query) ||
      livreur.courier_matricule?.toLowerCase().includes(query)
    );
  }, [livreurs, searchQuery]);

  useEffect(() => {
    loadLivreurs();
    loadCities();
  }, []);

  const loadLivreurs = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/by-role/livreur`);
      setLivreurs(response.data);
    } catch (error) {
      logClientError('admin.manage_livreurs.load', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      const response = await axios.get(`${API}/cities`);
      setCities(response.data);
    } catch (error) {
      logClientError('admin.manage_livreurs.load_cities', error);
    }
  };

  const handleCreate = () => {
    setEditingLivreur(null);
    setFormData({
      email: '',
      password: '',
      full_name: '',
      phone: '',
      city: ''
    });
    setShowDialog(true);
  };

  const handleEdit = (livreur) => {
    setEditingLivreur(livreur);
    setFormData({
      email: livreur.email,
      password: '',
      full_name: livreur.full_name,
      phone: livreur.phone,
      city: livreur.city || ''
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingLivreur) {
        // Update
        await axios.patch(`${API}/admin/users/${editingLivreur.id}`, {
          email: formData.email,
          full_name: formData.full_name,
          phone: formData.phone,
          city: formData.city
        });
        toast.success('Livreur mis à jour');
      } else {
        // Create
        await axios.post(`${API}/admin/users/create`, {
          ...formData,
          role: 'livreur'
        });
        toast.success('Livreur créé avec succès');
      }
      setShowDialog(false);
      loadLivreurs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleActive = async (livreurId, isActive) => {
    try {
      await axios.patch(`${API}/admin/users/${livreurId}/toggle-active?is_active=${!isActive}`);
      toast.success(isActive ? 'Livreur suspendu' : 'Livreur activé');
      loadLivreurs();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDelete = async (livreurId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce livreur ?')) return;

    try {
      await axios.delete(`${API}/admin/users/${livreurId}`);
      toast.success('Livreur supprimé');
      loadLivreurs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Truck className="w-6 h-6 text-red-600" />
                Gestion des Livreurs
              </CardTitle>
              <CardDescription>Créer, modifier et gérer les livreurs</CardDescription>
            </div>
            <Button
              data-testid="create-livreur-btn"
              className="bg-red-500 hover:bg-red-600"
              onClick={handleCreate}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Livreur
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search Bar */}
          {livreurs.length > 0 && (
            <div className="mb-6">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Rechercher par nom, email, téléphone ou ville..."
                className="max-w-md"
              />
              {searchQuery && (
                <p className="text-sm text-gray-600 mt-2">
                  {filteredLivreurs.length} résultat(s) trouvé(s)
                </p>
              )}
            </div>
          )}

          {livreurs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun livreur pour le moment</p>
            </div>
          ) : filteredLivreurs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>Aucun résultat trouvé pour "{searchQuery}"</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Matricule</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLivreurs.map((livreur) => (
                  <TableRow key={livreur.id}>
                    <TableCell className="font-medium">{livreur.full_name}</TableCell>
                    <TableCell>{livreur.courier_matricule || '-'}</TableCell>
                    <TableCell>{livreur.email}</TableCell>
                    <TableCell>{livreur.phone}</TableCell>
                    <TableCell>{livreur.city || 'Non définie'}</TableCell>
                    <TableCell>
                      {livreur.is_active ? (
                        <Badge className="bg-green-100 text-green-700 flex items-center gap-1 w-fit">
                          <CheckCircle className="w-3 h-3" />
                          Actif
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 flex items-center gap-1 w-fit">
                          <XCircle className="w-3 h-3" />
                          Suspendu
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`edit-livreur-${livreur.id}`}
                          onClick={() => handleEdit(livreur)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`toggle-livreur-${livreur.id}`}
                          onClick={() => handleToggleActive(livreur.id, livreur.is_active)}
                        >
                          {livreur.is_active ? (
                            <Ban className="w-4 h-4 text-orange-600" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`delete-livreur-${livreur.id}`}
                          onClick={() => handleDelete(livreur.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingLivreur ? 'Modifier le Livreur' : 'Nouveau Livreur'}
            </DialogTitle>
            <DialogDescription>
              {editingLivreur ? 'Modifier les informations du livreur' : 'Créer un nouveau compte livreur'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="full_name">Nom complet *</Label>
              <Input
                id="full_name"
                data-testid="livreur-full-name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                data-testid="livreur-email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            {!editingLivreur && (
              <div>
                <Label htmlFor="password">Mot de passe *</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="livreur-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingLivreur}
                />
              </div>
            )}

            <div>
              <Label htmlFor="phone">Téléphone *</Label>
              <Input
                id="phone"
                data-testid="livreur-phone"
                placeholder="+242..."
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>Ville *</Label>
              <Select
                value={formData.city}
                onValueChange={(val) => setFormData({ ...formData, city: val })}
              >
                <SelectTrigger data-testid="livreur-city">
                  <SelectValue placeholder="Sélectionnez une ville" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map(city => (
                    <SelectItem key={city.code} value={city.code}>{city.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="submit-livreur" className="bg-red-500 hover:bg-red-600">
                {editingLivreur ? 'Modifier' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageLivreurs;
